import React, { useEffect, useMemo, useState } from 'react';
import { X, Pencil, Coins, Plus, Award } from 'lucide-react';
import MollyPortrait from './MollyPortrait';
import ChessRook from './ChessRook';
import thumb from '../utils/thumb';

// ──────────────────────────────────────────────────────────────────────
// PR Manager (Molly) profile sheet.
//   - Header: portrait + editable name + role
//   - Level + XP progress bar + a placeholder "Skills" button
//   - Slot grid (1..5 cells). Filled cells show the painting thumb.
//     Empty cells are "+" tap targets that open the picker.
//   - "За работу" CTA appears when ≥1 painting is queued AND none are
//     already assigned (the queue is local until the player commits).
//     Cost is the queue length × per-painting fee. We commit them
//     sequentially via the parent's `onCommitAssignment` callback.
// ──────────────────────────────────────────────────────────────────────

const fmt = (n) => Number(n).toLocaleString();

const SlotCell = ({ entry, active, onClick, onClear, language }) => {
  if (!entry) {
    return (
      <button
        data-testid="pr-slot-empty"
        onClick={onClick}
        className="relative aspect-square rounded-xl border-2 border-dashed border-[#7DD3FC]/40 bg-[#7DD3FC]/[0.04] hover:border-[#7DD3FC]/70 hover:bg-[#7DD3FC]/[0.08] active:scale-95 transition-all flex flex-col items-center justify-center text-[#7DD3FC]/70 hover:text-[#7DD3FC]"
      >
        <Plus className="w-6 h-6 mb-0.5" />
        <span className="text-[9px] uppercase tracking-[0.15em]">
          {language === 'ru' ? 'добавить' : 'add'}
        </span>
      </button>
    );
  }
  return (
    <div
      data-testid={`pr-slot-${entry.card_id}`}
      className={`relative aspect-square rounded-xl overflow-hidden border-2 ${
        active ? 'border-[#7DD3FC] shadow-[0_0_14px_rgba(125,211,252,0.45)]' : 'border-[#7DD3FC]/55'
      }`}
    >
      <img
        src={thumb(entry.image_url, 160)}
        alt={entry.title || ''}
        className="absolute inset-0 w-full h-full object-cover"
        style={{ objectPosition: `${entry.image_focus_x ?? 50}% ${entry.image_focus_y ?? 50}%` }}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/15 to-transparent" />
      <div className="absolute inset-x-1 bottom-1">
        <div className="text-[10px] text-white font-semibold truncate">{entry.title}</div>
        <div className="text-[9px] text-[#7DD3FC] flex items-center gap-0.5 font-mono tabular-nums">
          <ChessRook className="w-2.5 h-2.5" />+{entry.estimated_card_xp} XP
        </div>
      </div>
      {/* Pending (not yet committed) cells get a × button so the
          player can swap their pick before paying. Committed
          assignments don't show this — they're locked until the
          painting returns from the exhibit. */}
      {!active && onClear && (
        <button
          data-testid={`pr-slot-clear-${entry.card_id}`}
          onClick={(e) => { e.stopPropagation(); onClear(); }}
          className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/65 backdrop-blur-sm text-white flex items-center justify-center hover:bg-black/85 transition-colors"
        >
          <X className="w-3 h-3" />
        </button>
      )}
      {active && (
        <span
          data-testid={`pr-slot-active-${entry.card_id}`}
          className="absolute top-1 right-1 px-1.5 py-[1px] rounded-full bg-[#7DD3FC] text-[#062436] text-[8px] font-bold uppercase tracking-wider"
        >
          ON
        </span>
      )}
    </div>
  );
};

const PRManagerSheet = ({
  open,
  language = 'en',
  state, // {name, level, xp, xp_to_next, slots, fee_coins, assignments, eligible_cards, ...}
  userCoins = 0,
  onClose,
  onRequestRename,
  onRequestPicker,        // () -> Promise<eligibleCard|null> ; parent opens picker and resolves with chosen card
  onCommitAssignment,     // async (card_id) -> { user, ... }
}) => {
  const [shouldRender, setShouldRender] = useState(open);
  const [pendingCards, setPendingCards] = useState([]); // local queue before commit
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) { setShouldRender(true); return; }
    if (!shouldRender) return;
    const id = setTimeout(() => setShouldRender(false), 220);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!shouldRender) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [shouldRender]);

  // Reset local queue when the sheet closes so a fresh open starts
  // with a clean slate.
  useEffect(() => {
    if (!open) setPendingCards([]);
  }, [open]);

  // Slots grid: active assignments first, then pending picks, then
  // empty "+" cells up to slotsTotal. Computed BEFORE the early
  // shouldRender return so the hook order stays stable.
  const assignmentsForCells = state?.assignments || [];
  const slotsTotalForCells = state?.slots || 1;
  const cells = useMemo(() => {
    const out = [];
    assignmentsForCells.forEach((a) => out.push({ kind: 'active', entry: a }));
    pendingCards.forEach((p) => out.push({ kind: 'pending', entry: p }));
    while (out.length < slotsTotalForCells) out.push({ kind: 'empty' });
    return out;
  }, [assignmentsForCells, pendingCards, slotsTotalForCells]);

  if (!shouldRender) return null;

  const exiting = !open;
  const name = state?.name || 'Molly';
  const level = state?.level || 1;
  const xp = state?.xp || 0;
  const xpToNext = state?.xp_to_next || 25;
  const slotsTotal = slotsTotalForCells;
  const feeCoins = state?.fee_coins || 200;
  const assignments = assignmentsForCells;
  const slotsUsed = assignments.length;
  const slotsFree = Math.max(0, slotsTotal - slotsUsed);

  const totalCost = pendingCards.length * feeCoins;
  const totalXp = pendingCards.reduce((s, c) => s + (c.estimated_card_xp || 0), 0);
  const affordable = userCoins >= totalCost;
  const canCommit = pendingCards.length > 0 && affordable && !submitting;

  const close = () => {
    if (submitting) return;
    onClose && onClose();
  };

  const addToQueue = async () => {
    if (slotsFree - pendingCards.length <= 0) return;
    if (!onRequestPicker) return;
    // Exclude already-queued and already-assigned card ids.
    const excluded = new Set([
      ...assignments.map((a) => a.card_id),
      ...pendingCards.map((p) => p.card_id),
    ]);
    const picked = await onRequestPicker(excluded);
    if (picked) setPendingCards((p) => [...p, picked]);
  };

  const removeFromQueue = (cardId) => {
    setPendingCards((p) => p.filter((c) => c.card_id !== cardId));
  };

  const commit = async () => {
    if (!canCommit) return;
    setSubmitting(true);
    let allOk = false;
    try {
      // Commit each queued assignment serially. If one fails, we keep
      // the rest in the queue so the player can retry.
      const remaining = [...pendingCards];
      while (remaining.length > 0) {
        const next = remaining[0];
        await onCommitAssignment(next.card_id);
        remaining.shift();
        setPendingCards([...remaining]);
      }
      allOk = true;
    } finally {
      setSubmitting(false);
    }
    // Drop the player back to the collection view once everything was
    // assigned. Clear the local queue first so the parent's
    // `refreshPrState()` (which arrives after onClose() resolves the
    // backend assignments) doesn't briefly re-render this sheet with
    // pending → active cells *during* the close animation — that swap
    // looks like a second frame / glitch.
    if (allOk) {
      setPendingCards([]);
      onClose && onClose();
    }
  };

  const progressPct = Math.min(100, Math.round((xp / xpToNext) * 100));

  const t = language === 'ru'
    ? {
        role: 'Агент · команда',
        subline: 'Агент увеличивает влияние картин с выставок',
        level: 'Уровень',
        skills: 'Навыки',
        skillsSoon: 'Скоро',
        slotsTitle: 'Картины под сопровождением',
        slotsCaption: (used, total) => `${used} из ${total}`,
        ctaIdle: 'Выберите картину',
        ctaPay: 'За работу',
        ctaNoMoney: 'Не хватает монет',
        bonus: 'Бонус',
        committedHint: 'XP начислится после возвращения с выставки',
      }
    : {
        role: 'Agent · team',
        subline: 'The agent increases the influence of paintings from exhibitions',
        level: 'Level',
        skills: 'Skills',
        skillsSoon: 'Soon',
        slotsTitle: 'Paintings under agent',
        slotsCaption: (used, total) => `${used} of ${total}`,
        ctaIdle: 'Pick a painting',
        ctaPay: 'Get to work',
        ctaNoMoney: 'Not enough coins',
        bonus: 'Bonus',
        committedHint: 'XP applied when the exhibit ends',
      };

  return (
    <div
      data-testid="pr-manager-sheet"
      className={`fixed inset-0 z-[60] flex items-end sm:items-center justify-center ${
        exiting ? 'animate-out fade-out duration-200' : 'animate-in fade-in duration-200'
      }`}
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-[2px]" onClick={close} />
      <div
        className={`relative w-full sm:max-w-sm bg-[#0E0E18] border border-[#7DD3FC]/25 rounded-t-2xl sm:rounded-2xl shadow-[0_-8px_30px_rgba(0,0,0,0.6)] px-4 pt-3 pb-5 ${
          exiting ? 'animate-out slide-out-to-bottom duration-200' : 'animate-in slide-in-from-bottom duration-300'
        }`}
        style={{ paddingBottom: 'max(20px, env(safe-area-inset-bottom))' }}
      >
        <div className="mx-auto w-10 h-1 rounded-full bg-[#3F3F46]/70 mb-3 sm:hidden" />

        {/* Header: portrait + name + role */}
        <div className="flex items-start gap-3 mb-3">
          <div className="relative w-14 h-14 rounded-full bg-[#7DD3FC]/10 border border-[#7DD3FC]/40 flex items-center justify-center overflow-hidden shrink-0">
            <div style={{ transform: 'translateY(-2px)' }}>
              <MollyPortrait size={48} />
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <button
              data-testid="pr-name-edit"
              onClick={onRequestRename}
              className="group inline-flex items-center gap-1.5 text-left"
            >
              <span className="text-[18px] font-semibold text-white leading-tight">{name}</span>
              <Pencil className="w-3 h-3 text-[#A1A1AA]/60 group-hover:text-[#7DD3FC] transition-colors" />
            </button>
            <div className="text-[10px] text-[#A1A1AA]/70 uppercase tracking-[0.18em] mt-0.5">
              {t.role}
            </div>
            <div className="text-[12px] text-[#A1A1AA] mt-1">{t.subline}</div>
          </div>
          <button
            data-testid="pr-sheet-close"
            onClick={close}
            className="p-1 rounded-full hover:bg-white/5 transition-colors"
          >
            <X className="w-4 h-4 text-[#A1A1AA]" />
          </button>
        </div>

        {/* Level + XP bar + Skills button */}
        <div className="mb-4 px-3 py-2.5 rounded-xl bg-[#7DD3FC]/[0.06] border border-[#7DD3FC]/25 flex items-center gap-3">
          <div className="flex items-center gap-1.5 shrink-0">
            <Award className="w-4 h-4 text-[#7DD3FC]" />
            <span className="text-[12px] uppercase tracking-[0.16em] text-[#A1A1AA]/80">
              {t.level}
            </span>
            <span data-testid="pr-level" className="text-[14px] font-bold text-white tabular-nums">
              {level}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="h-1.5 rounded-full bg-[#0C0C14] border border-[#7DD3FC]/15 overflow-hidden">
              <div
                data-testid="pr-xp-bar"
                className="h-full bg-gradient-to-r from-[#7DD3FC] to-[#D4AF37] transition-all"
                style={{ width: `${progressPct}%` }}
              />
            </div>
            <div className="mt-1 flex items-center justify-between text-[10px] text-[#A1A1AA]/70 font-mono tabular-nums">
              <span>{xp} / {xpToNext} XP</span>
              <span>{progressPct}%</span>
            </div>
          </div>
          <button
            data-testid="pr-skills-btn"
            onClick={async () => {
              const { toast } = await import('sonner');
              toast(t.skillsSoon);
            }}
            className="shrink-0 px-2.5 py-1 rounded-md border border-[#7DD3FC]/35 text-[10px] uppercase tracking-[0.14em] text-[#7DD3FC]/85 hover:bg-[#7DD3FC]/10 transition-colors"
          >
            {t.skills}
          </button>
        </div>

        {/* Slots grid */}
        <div className="flex items-baseline justify-between mb-2">
          <span className="text-[11px] uppercase tracking-[0.16em] text-[#A1A1AA]/70">
            {t.slotsTitle}
          </span>
          <span className="text-[10px] text-[#A1A1AA]/60 font-mono tabular-nums">
            {t.slotsCaption(slotsUsed + pendingCards.length, slotsTotal)}
          </span>
        </div>
        <div className="grid grid-cols-3 gap-2 mb-4">
          {cells.map((cell, i) => {
            if (cell.kind === 'empty') {
              return (
                <SlotCell
                  key={`empty-${i}`}
                  entry={null}
                  active={false}
                  onClick={addToQueue}
                  language={language}
                />
              );
            }
            return (
              <SlotCell
                key={cell.entry.card_id}
                entry={cell.entry}
                active={cell.kind === 'active'}
                onClear={cell.kind === 'pending' ? () => removeFromQueue(cell.entry.card_id) : null}
                language={language}
              />
            );
          })}
        </div>

        {/* CTA — always shows price + XP preview once at least one
            card is queued. Button is disabled (greyed) when the player
            can't afford the total cost, so the missing-coins state is
            communicated by the button look, not by a copy swap. */}
        <button
          data-testid="pr-commit-btn"
          onClick={commit}
          disabled={!canCommit}
          className={`w-full py-3 rounded-xl font-bold text-[14px] transition-all flex items-center justify-center gap-2 ${
            canCommit
              ? 'bg-gradient-to-r from-[#7DD3FC] to-[#D4AF37] text-[#062436] shadow-[0_0_22px_rgba(125,211,252,0.45)] hover:brightness-110 active:scale-[0.98]'
              : 'bg-[#141420] text-[#A1A1AA]/70 border border-[#3F3F46]/60 cursor-not-allowed'
          }`}
        >
          {pendingCards.length === 0 ? (
            <span>{t.ctaIdle}</span>
          ) : (
            <>
              <span>{t.ctaPay}</span>
              <span className={`flex items-center gap-1 font-mono ${canCommit ? '' : 'text-[#EF4444]/85'}`}>
                <Coins className="w-4 h-4" />
                {fmt(totalCost)}
              </span>
              <span className={canCommit ? 'text-[#062436]/55' : 'text-[#A1A1AA]/40'}>·</span>
              <span className="flex items-center gap-0.5 font-mono">
                <ChessRook className="w-3.5 h-3.5" />
                +{totalXp}
              </span>
            </>
          )}
        </button>
        {assignments.length > 0 && (
          <p className="mt-2 text-[10px] text-[#A1A1AA]/55 text-center leading-snug">
            {t.committedHint}
          </p>
        )}
      </div>
    </div>
  );
};

export default PRManagerSheet;
